import SEO from '@/components/shared/SEO'
import CTAButton from '@/components/shared/CTAButton'
import Reviews from '@/components/Reviews'
import KeyInformation from '@/components/KeyInformation'
import USPBanner from '@/components/USPBanner'
import TrustpilotBadge from '@/components/TrustpilotBadge'

const AboutPage = () => {
  return (
    <div className="flex-1">
      <SEO
        title="About Claims1 - Professional Car Finance Claims Management | UK"
        description="Learn about Claims1, a professional claims management company with extensive experience helping UK customers recover money from mis-sold car finance agreements. £449M+ recovered."
        keywords="Claims1, car finance claims company, professional claims management, mis-sold finance recovery, UK claims specialists"
        url="/about-us"
        image="/Image/about-us-claims1-car-finance-claim2.webp"
        imageAlt="Claims1 professional claims management team"
        schemaType="AboutPage"
        schemaData={{
          mainEntity: {
            '@type': 'Organization',
            name: 'Claims1',
            description:
              'Professional claims management company specializing in car finance mis-selling claims',
            url: 'https://claims1.co.uk',
            foundingDate: '2015',
            areaServed: 'United Kingdom',
            serviceType: 'Claims Management Services',
            knowsAbout: [
              'Car Finance Claims',
              'PCP Claims',
              'HP Claims',
              'Financial Mis-selling',
            ],
            hasCredential: 'FCA Regulated',
          },
        }}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'About Us', url: '/about-us' },
        ]}
      />

      <USPBanner />

      <div
        className="relative flex min-h-[70vh] items-center md:min-h-[91vh]"
        style={{
          backgroundImage:
            'url("/Image/about-us-claims1-car-finance-claim2.webp")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="absolute inset-0 bg-black/53"></div>
        <div className="relative z-10 container mx-auto px-4 py-8">
          {/* Text Content Section */}
          <div className="px-4 text-center lg:px-20">
            <h1 className="mb-4 text-center text-4xl font-black tracking-tight text-white uppercase sm:mb-6 sm:text-6xl">
              We&apos;re on a mission to help our customers get justice for
              mis-sold vehicle finance.
            </h1>
            <p className="font-raleway-numbers mx-auto mb-4 max-w-xl text-lg leading-relaxed font-bold text-white/90 sm:text-xl md:mb-6 md:max-w-3xl lg:text-2xl">
              Welcome to Claims1. We&apos;re a well-established claims
              management company with years of experience. We help customers get
              back money that&apos;s rightfully theirs.
            </p>
            <div className="mt-6 flex justify-center">
              <CTAButton />
            </div>
            <div className="mt-6 flex justify-center md:mt-12">
              <TrustpilotBadge rating="4star" />
            </div>
          </div>
        </div>
      </div>

      {/* Body Section */}
      <div className="bg-white pt-12 lg:pt-16">
        <div className="container mx-auto px-4">
          <div className="mx-auto mb-6 max-w-4xl text-center sm:mb-8">
            <p className="mb-4 text-base text-[#002020] sm:mb-6 sm:text-lg">
              For years, banks and finance companies have been mis-selling
              products to customers across the UK. Too often, these products
              weren&apos;t properly explained, or were added without customers
              even knowing about them. That&apos;s exactly why we exist to put
              things right.
            </p>
            <p className="mb-4 text-base text-[#002020] sm:mb-6 sm:text-lg">
              The latest scandal involves car and motor finance. A recent
              Financial Conduct Authority (FCA) investigation uncovered
              widespread mis-selling throughout the industry. The problem?
              Lenders were secretly paying brokers incentives, while dealers
              inflated interest rates to earn bigger commissions. All of this
              was happening completely behind customers&apos; backs.
            </p>
            <p className="mb-4 text-base text-[#002020] sm:mb-6 sm:text-lg">
              If you have financed a vehicle since{' '}
              <span className="font-raleway-numbers">2007</span>, there&apos;s a
              good chance you could have been affected. With our proven track
              record in financial mis-selling claims, we&apos;re here to help
              you recover any money you may be owed.
            </p>
            <div className="mt-6 flex justify-center">
              <CTAButton />
            </div>
            <p className="mt-4 text-center text-sm text-[#002020]">
              For more FCA updates, see our Information Sources
              <sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>
                4
              </sup>{' '}
              below.
            </p>
          </div>
        </div>
      </div>

      {/* About Banner Section - Similar to Homepage Missold Banner */}
      <section className="pt-6">
        <div className="flex min-h-[300px] flex-col sm:min-h-[350px] lg:min-h-[300px] lg:flex-row">
          {/* Text - Left Side */}
          <div
            className="order-2 flex items-center justify-center px-4 py-8 sm:px-6 sm:py-10 lg:order-1 lg:w-1/2 lg:px-20 lg:py-12"
            style={{ backgroundColor: '#c0e0e0' }}
          >
            <div className="max-w-2xl text-center sm:text-left">
              <p className="text-foreground text-2xl leading-relaxed font-bold">
                Navigating the claims process can be complicated and lengthy,
                which is why Claims1 is committed to making this process as
                hassle free as possible, so you can focus on life&apos;s
                priorities.
              </p>
            </div>
          </div>

          {/* Photo - Right Side */}
          <div className="relative order-1 h-80 sm:h-96 lg:order-2 lg:h-auto lg:w-1/2">
            <img
              src="/Image/sit-back-relax-claims1-car-finance-claim.webp"
              alt="Professional claims management - Claims1 experts"
              className="h-full w-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white pt-16 lg:pt-20">
        <div className="container mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2
            className="mb-8 text-center text-4xl font-black sm:mb-12 sm:text-5xl"
            style={{ color: 'oklch(0.2205 0.0376 194.77)' }}
          >
            Why Choose Us?
          </h2>

          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="order-2 space-y-6 lg:order-1 lg:space-y-8">
              <div className="group border-foreground flex cursor-pointer items-start gap-5 rounded-xl border-2 bg-white p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mt-0.5 mr-4 h-10 w-10 flex-shrink-0 text-[#ff8904] transition-transform duration-300 group-hover:scale-110"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                <p className="text-lg leading-relaxed font-medium text-[#002020]">
                  We pride ourselves in helping you get back what is rightfully
                  yours.
                </p>
              </div>

              <div className="group border-foreground flex cursor-pointer items-start gap-5 rounded-xl border-2 bg-white p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mt-0.5 mr-4 h-10 w-10 flex-shrink-0 text-[#ff8904] transition-transform duration-300 group-hover:scale-110"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  ></path>
                </svg>
                <p className="text-lg leading-relaxed font-medium text-[#002020]">
                  Claims1 is a well-established Claims Management Company with
                  many years&apos; experience of financial mis-selling claims.
                </p>
              </div>

              <div className="group border-foreground flex cursor-pointer items-start gap-5 rounded-xl border-2 bg-white p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mt-0.5 mr-4 h-10 w-10 flex-shrink-0 text-[#ff8904] transition-transform duration-300 group-hover:scale-110"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                  ></path>
                </svg>
                <p className="text-lg leading-relaxed font-medium text-[#002020]">
                  We have developed highly detailed processes to take the hassle
                  out of the claims process.
                </p>
              </div>

              <div className="group border-foreground flex cursor-pointer items-start gap-5 rounded-xl border-2 bg-white p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <div className="mt-0.5 mr-4 h-10 w-10 flex-shrink-0 text-[#ff8904] transition-transform duration-300 group-hover:scale-110">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mt-0.5 mr-4 h-10 w-10 flex-shrink-0 text-[#ff8904] transition-transform duration-300 group-hover:scale-110"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    ></path>
                  </svg>
                </div>
                <p className="text-lg leading-relaxed font-medium text-[#002020]">
                  You&apos;ll find our team to be professional, courteous, and
                  friendly.
                </p>
              </div>

              <div className="group border-foreground flex cursor-pointer items-start gap-5 rounded-xl border-2 bg-white p-6 transition-all duration-300 hover:scale-105 hover:shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="mt-0.5 mr-4 h-10 w-10 flex-shrink-0 text-[#ff8904] transition-transform duration-300 group-hover:scale-110"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
                <p className="text-lg leading-relaxed font-medium text-[#002020]">
                  You only need to provide us with a few key details and then we
                  and our partners will do the rest. It really couldn&apos;t be
                  any simpler!
                </p>
              </div>
            </div>

            <div className="relative order-1 lg:order-2">
              <div className="relative h-[500px] w-full overflow-hidden rounded-2xl sm:h-[600px] lg:h-[700px]">
                <img
                  src="/Image/why-cloose-claims1-car-finance-calim.webp"
                  alt="Why choose Claims1"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>

        {/* Final CTA Section */}
        <div className="bg-white py-12 lg:py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="mb-6 text-3xl font-black text-[#002020] sm:text-4xl">
              Ready to Start Your Claim?
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-lg text-[#002020]">
              Let our experienced team help you recover the money you could be
              owed from your car finance agreement.
            </p>
            <CTAButton />
          </div>
        </div>

        {/* Reviews Section */}
        <Reviews />

        {/* Key Facts Section */}
        <KeyInformation />
      </div>
    </div>
  )
}

export default AboutPage
